import {useEffect, useState} from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import StandardDialog from '../../common/StyledComponents/StandardDialog';
import PrimaryButton from '../../common/StyledComponents/PrimaryButton';
import SecondaryButton from '../../common/StyledComponents/SecondaryButton';
import {Switch, Route, Redirect, useHistory} from 'react-router-dom';
import {API_URL, SERVER_URL} from '../../constants';
import {
  resetSecuredFetchRequest,
  securedFetchRequest,
} from '@bidmii/common/lib/util/FetchRequest';
import {isDelegate} from '@bidmii/common/lib/util/SubLoginUtil';
import ProtectedRoute, {hasActiveAuthSignal} from '../../common/ProtectedRoute';
import {useRecoilState, useRecoilValue, useSetRecoilState} from 'recoil';
import {
  userState,
  userStatsState,
  userProductsState,
  firestoreUserState,
  miscUserDetailsState,
  userLikesState,
  userDislikesState,
} from '../../recoil/user/userAtom';
import LogRocket from 'logrocket';
import * as Sentry from '@sentry/react';
import clearStorageOnSignOut from '../../util/ClearStorageOnSignOut';
import {apiRootState} from '../../recoil/rootAtom';
import {lastMsgState} from '../../recoil/socket/socketAtom';
import {authUserState} from '../../recoil/auth/AuthRecoil';
import useRecordSession from './hooks/useRecordSession';
import {leftMenuOpenState} from '../../recoil/navigation/NavigationRecoil';
import {
  filterValuesState,
  savedProjectsFilterState,
} from '../../recoil/filterValues/filterValuesAtom';
import {
  LazyReviewWizard,
  LazySupport,
  LazyCategoriesList,
  LazyLocationsList,
  LazySupportPopup,
  LazyMain,
  LazyMainContainer,
  LazyMarketingPage,
  LazyHomeownerDashboard,
  LazyContractorDashboard,
  LazyProWebsite,
  LazyLogin,
  LazyFormsRouter,
  LazyNewUserRouter,
  LazySubscriptionsCheckout,
  LazyBidmiiProLandingPage,
} from '../../common/LazyLoader';
import {firebaseAuth, firestoreDb} from '../../App';
import {signOut} from 'firebase/auth';
import {collection, doc, getDoc, setDoc} from 'firebase/firestore';
import useRefreshUserState from '../../hooks/useRefreshUserState';
import useSnackbar from '../../hooks/useSnackbar';
import UploadFailureSnackbarListener from '../../common/UploadFailureSnackbarListener';
import BidmiiCustomLoader from '../BidmiiCustomLoader/BidmiiCustomLoader';
import {getUserDataByLink} from './utils/getUserDataByLink';
import {loadingState} from '../../recoil/bidmiiCustomLoader/bidmiiCustomLoaderAtom';
import {isContractor, isHomeowner} from '../../util/UserUtils';
import useCelebrationLottie from '../../hooks/useCelebrationLottie';
import {ServiceOrderCheckout} from '../ServiceOrders/ServiceOrderCheckout';
import StripeAccountSettings from '../Profile/EditProfile/components/StripeConnectAccount/StripeAccountSettings';
import ReviewWorkflow from '../ReviewWizard/ReviewWorkflow';

const MainRouter = () => {
  const [user, setUser] = useRecoilState(userState);
  const lastMsg = useRecoilValue(lastMsgState);
  const history = useHistory();
  const setLoading = useSetRecoilState(loadingState);
  const setUserLikes = useSetRecoilState(userLikesState);
  const setUserDislikes = useSetRecoilState(userDislikesState);
  const setUserStats = useSetRecoilState(userStatsState);
  const setUserProducts = useSetRecoilState(userProductsState);
  const setMiscUserDetails = useSetRecoilState(miscUserDetailsState);
  const setAuthUser = useSetRecoilState(authUserState);
  const apiRoot = useRecoilValue(apiRootState);
  const recordSession = useRecordSession();
  const setLeftMenuOpen = useSetRecoilState(leftMenuOpenState);
  const setSavedProjectsFilter = useSetRecoilState(savedProjectsFilterState);
  const [firestoreUser, setFirestoreUser] = useRecoilState(firestoreUserState);
  const setFilterValues = useSetRecoilState(filterValuesState);
  const refreshUser = useRefreshUserState();
  const {BidmiiSnackbar, openSnackbar} = useSnackbar();
  const [celebrationAnimation, startCelebration] = useCelebrationLottie({});
  // A direct-quote claim link (?migrate=true) was opened while signed in with a
  // non-homeowner account — explain instead of hijacking them into homeowner signup.
  const [wrongAccountForQuoteOpen, setWrongAccountForQuoteOpen] =
    useState(false);

  useEffect(() => {
    try {
      const type = (lastMsg as $TSFixMe)?.type;
      const model = (lastMsg as $TSFixMe)?.model;

      if (type === 'user_banned') {
        signOut(firebaseAuth)
          .then(() => {
            clearStorageOnSignOut();
            // eslint-disable-next-line no-alert
            window.alert(
              'Your account has been temporarily disabled, please contact your administrator at 416 628 1553',
            );
            setUser(null as any);
            setSavedProjectsFilter(false);
            history.push('/login');
          })
          .catch((err) => {
            console.warn(err);
            Sentry.withScope(function (scope) {
              scope.setTag('section', 'MainRouter.tsx useEffect()');
              Sentry.captureException(err);
            });
          });
      } else if (model === 'User' && type === 'update') {
        refreshUser();
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [lastMsg, history, setUser, setSavedProjectsFilter, refreshUser]);

  useEffect(() => {
    if (apiRoot != null) {
      firebaseAuth.onAuthStateChanged((authUser) => {
        handleUserStateChange(authUser);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRoot]);

  useEffect(() => {
    // do not bother redirecting if user is on login page or in new user flow
    //
    // Sub-logins: a delegate is joining an ALREADY-verified company rather than applying to
    // Bidmii, so none of the onboarding redirects below apply to them. Their first sign-in still
    // sets isNewUser, which would otherwise drop them into the contractor application /
    // identity-verification / category wizard and ask them to re-apply as a business they are
    // not. Skipping the whole block lands them straight in the app.
    // isDelegate() is false for every account that is not a sub-login (parentUser is null for
    // 100% of accounts today), so this leaves the flow exactly as-is for everyone else.
    if (
      user?.uid &&
      !isDelegate(user) &&
      window.location.pathname !== '/login' &&
      !window.location.pathname.includes('/new/user')
    ) {
      // if there is no user type and user is not already in signup flow, redirect to user type choice step
      if (!user?.userType && !localStorage.getItem('userType')) {
        history.push('/new/user/signup/user-type');
        return;
      }

      if (sessionStorage.getItem('isNewUser')) {
        if (
          isHomeowner(user) ||
          localStorage.getItem('anonymousProjectCreated') !== null
        ) {
          history.push('/new/user/signup/homeowner/1');
        } else if (isContractor(user)) {
          history.push(
            '/new/user/application/contractor/1?newApplication=true',
          );
        } else {
          history.push('/new/user/signup/user-type');
        }
        return;
      }

      if (firestoreUser && firestoreUser.newUserWizardStep) {
        // Homeowners who already agreed to terms have a stale wizard step from the old flow — clear it
        if (isHomeowner(user) && user?.hasAgreedToTerms) {
          const userDocRef = doc(collection(firestoreDb, 'users'), user.uid);
          setDoc(userDocRef, {newUserWizardStep: null}, {merge: true});
        } else if (user?.userType === 'BIDDER' && user?.hasAgreedToTerms) {
          // Contractor already completed onboarding but has stale wizard step — clear it
          const userDocRef = doc(collection(firestoreDb, 'users'), user.uid);
          setDoc(userDocRef, {newUserWizardStep: null}, {merge: true});
        } else {
          process.env.NODE_ENV === 'development' &&
            console.log(
              'newUserWizardStep :>> ',
              firestoreUser.newUserWizardStep,
            );

          // Normalize stale wizard steps from old 15-step flow to new 6-step flow
          const wizardStep = firestoreUser.newUserWizardStep;
          const normalizedStep =
            user?.userType === 'BIDDER' &&
            (typeof wizardStep === 'string' || wizardStep > 6)
              ? 2
              : wizardStep;

          user?.userType === 'BIDDER'
            ? history.push(`/new/user/application/contractor/${normalizedStep}`)
            : history.push(
                `/new/user/signup/homeowner/${firestoreUser.newUserWizardStep}`,
              );
          return;
        }
      }
    }

    if (localStorage.getItem('bidmiiProUid')) {
      // Signed out: stay on the login screen so an existing account holder can just sign in.
      // This block used to run for signed-out visitors too and pushed EVERYONE into the
      // homeowner signup wizard — someone whose email already had an account was told to
      // create a new one, and obeying would have attached the quote to an empty duplicate
      // account (2026-08-31 direct-quote incident).
      if (!user?.uid) {
        return;
      }
      if (user?.userType === 'REQUESTER') {
        const migrateTarget = localStorage.getItem('bidmiiProUid');
        // Consume the key: leaving it set replayed this redirect on every later sign-in.
        localStorage.removeItem('bidmiiProUid');
        history.push(`${migrateTarget}?migrate=true`);
      } else if (user?.userType) {
        // Signed in with a contractor/admin account: the quote can only live in a
        // homeowner account, so explain rather than hijack them into homeowner signup.
        setWrongAccountForQuoteOpen(true);
      } else {
        // No user type yet: a genuinely new visitor — homeowner signup is correct for them.
        history.push('/new/user/signup/homeowner/1');
      }
      return;
    }

    const urlSearchParams = new URLSearchParams(window.location.search);
    const redirect = urlSearchParams.get('redirect');
    if (
      sessionStorage.getItem('signInSuccessUrl') ||
      (redirect && !(window as $TSFixMe).OVERRIDE_USER_ID)
    ) {
      const signInSuccessUrl = sessionStorage.getItem('signInSuccessUrl');
      sessionStorage.removeItem('signInSuccessUrl');
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
      history.push(redirect || signInSuccessUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const migrateAnonymousUser = (userObject: $TSFixMe) => {
    process.env.NODE_ENV === 'development' &&
      console.log('MIGRATING ANONYMOUS USER');
    const anonymousUid = localStorage.getItem('anonymousUid');
    const hadAnonymousProject =
      localStorage.getItem('anonymousProjectCreated') !== null;

    // Clean up localStorage immediately to prevent re-triggering on failure
    localStorage.removeItem('anonymousUid');
    localStorage.removeItem('isAnonymous');
    localStorage.removeItem('anonymousProjectCreated');

    securedFetchRequest(`${API_URL}/user`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userType: hadAnonymousProject ? 'REQUESTER' : userObject.userType,
        uid: anonymousUid,
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        setUser({...userObject, ...res});
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);
        openSnackbar(
          "We couldn't attach your project to your new account. Please contact support and we'll recover it.",
          'error',
        );

        Sentry.withScope(function (scope) {
          scope.setUser({
            uid: user?.uid,
            firstName: user?.firstName,
          });
          scope.setTag('section', 'create_user_step_2');
          Sentry.captureException(err);
        });
      });
  };

  const handleUserStateChange = (authUser: $TSFixMe) => {
    setAuthUser(authUser);
    resetSecuredFetchRequest(
      authUser ? (forceRefresh) => authUser.getIdToken(forceRefresh) : null,
    );

    if (authUser) {
      if (authUser?.displayName) {
        const firstName = authUser.displayName.split(' ')[0];
        const lastName =
          authUser.displayName.split(' ').length > 1
            ? authUser.displayName.split(' ')[1]
            : '';
        sessionStorage.setItem('firstName', firstName);
        sessionStorage.setItem('lastName', lastName);
      }

      if (authUser.email) {
        sessionStorage.setItem('email', authUser.email);
      }

      authUser
        .getIdToken()
        .then((token: $TSFixMe) => {
          const tokenExpiry = new Date();
          tokenExpiry.setHours(tokenExpiry.getHours() + 1);
          sessionStorage.setItem('Firebase-Authorization', token);
          // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'Date' is not assignable to param... Remove this comment to see the full error message
          sessionStorage.setItem('Token-Expiry', tokenExpiry);
          sessionStorage.setItem('uid', authUser.uid);
          //postSessionData();

          // @ts-ignore
          const userDocRef = doc(
            collection(firestoreDb, 'users'),
            authUser.uid,
          );

          getDoc(userDocRef)
            .then((doc) => {
              if (doc.exists()) {
                const data = doc.data();
                setFirestoreUser(data);
                if (data?.filters) {
                  setFilterValues(data.filters);
                }
              } else {
                setDoc(userDocRef, {email: authUser.email}, {merge: true});
                setFirestoreUser({email: authUser.email});
                process.env.NODE_ENV === 'development' &&
                  console.log('Firestore user doc does not exist');
              }
            })
            .catch((err: any) => {
              process.env.NODE_ENV === 'development' && console.error(err);
              Sentry.withScope(function (scope) {
                scope.setTag(
                  'section',
                  'MainRouter.tsx handleUserStateChange()',
                );
                Sentry.captureException(err);
              });
            });

          if (!authUser.isAnonymous) {
            securedFetchRequest(
              `${
                // @ts-ignore
                apiRoot._links.currentUser.href
              }?view=lite&code=${authUser.uid.substr(0, 10)}`,
              {
                headers: {
                  'Content-Type': 'application/json',
                },
                priority: 'high',
              },
            )
              .then((userRes) => userRes.json())
              .then((userRes) => {
                setLoading(false);
                setUser(userRes);
                localStorage.setItem('user', JSON.stringify(userRes));

                if (
                  process.env.NODE_ENV !== 'development' && // JK: Disable log rocket while in development
                  process.env.REACT_APP_ENABLE_LOG_ROCKET === '1'
                ) {
                  LogRocket.getSessionURL(function (sessionURL) {
                    recordSession(userRes, sessionURL);
                  });

                  LogRocket.identify(userRes.id, {
                    name: userRes.name,
                    email: userRes.email,
                    userType: userRes.userType,
                  });
                }

                if (window.outerWidth <= 600) {
                  setLeftMenuOpen(false);
                }

                if (localStorage.getItem('anonymousUid')) {
                  migrateAnonymousUser(userRes);
                  return;
                }

                // The isNewUser suppression exists because a brand-new account finishes loading
                // at the end of the signup wizard (which clears the flag) instead of here.
                // Sub-logins: a delegate never enters that wizard -- they are joining an
                // already-verified company -- so nothing would ever clear their flag and they
                // would spend their whole first session on a half-loaded account: no
                // `initialised`, no stats/products/likes/user-details. They are fully onboarded
                // the moment they exist, so load their data now. No-op for non-delegates.
                if (
                  userRes._links &&
                  (!sessionStorage.getItem('isNewUser') || isDelegate(userRes))
                ) {
                  getAdditionalUserData(userRes._links);
                }
              })
              .catch((err: $TSFixMe) => {
                // Never leave the full-screen loader stuck if the post-login
                // currentUser fetch fails/times out — clear it and surface the error.
                setLoading(false);
                openSnackbar(
                  'We had trouble loading your account. Please refresh and try again.',
                  'error',
                );
                Sentry.withScope(function (scope) {
                  scope.setTag('section', 'MainRouter.tsx currentUser fetch');
                  Sentry.captureException(err);
                });
              });
          } else {
            setLoading(false);
          }
        })
        .catch((err: $TSFixMe) => {
          // getIdToken itself failed — don't strand the loader.
          setLoading(false);
          Sentry.withScope(function (scope) {
            scope.setTag('section', 'MainRouter.tsx getIdToken');
            Sentry.captureException(err);
          });
        });
    } else {
      setLoading(false);
    }
  };

  const getAdditionalUserData = async (links: any) => {
    const currentUser = await getUserDataByLink(
      // @ts-ignore
      apiRoot?._links.currentUser.href,
    );
    // A failed boot fetch resolves null; crashing here used to strand the
    // user on a half-loaded account until refresh.
    if (!currentUser) {
      return;
    }
    currentUser.initialised = true;
    setUser(currentUser);

    const stats = await getUserDataByLink(links.stats.href);
    setUserStats(stats);

    const products = await getUserDataByLink(
      links.products.href.replace('{&recId,recModel}', ''),
    );
    if (products?._embedded) {
      setUserProducts(products._embedded.models);
    }

    const likes = await getUserDataByLink(links.likes.href);
    setUserLikes(new Set(likes ?? []));

    if (user?.userType === 'BIDDER') {
      const dislikes = await getUserDataByLink(`${API_URL}/users/dislikes`);
      setUserDislikes(new Set(dislikes ?? []));
    }

    const miscDetails = await getUserDataByLink(`${API_URL}/user/user-details`);
    if (miscDetails?._embedded) {
      setMiscUserDetails(miscDetails._embedded.models);
    }

    paymentSuccessFeedback();
  };

  const paymentSuccessFeedback = () => {
    const successMessages = {
      bidmii_pro: 'You have successfully subscribed to Bidmii Pro!',
      bidmii_pro_scholarship:
        'You have successfully applied to the Bidmii Scholarship!',
      project_unlock: 'This project has been successfully unlocked!',
      project_promotion: 'Your project will be promoted for 7 days.',
      stage_funded: 'Stage funded! The contractor will be notified.',
      bid_range: 'Bid range successfully unlocked for this project.',
      id_verification: 'Verification payment successful',
    };

    const params = new URLSearchParams(window.location.search);
    const redirectStatus = params.get('redirect_status');
    const paymentType = params.get('payment_type');
    if (redirectStatus === 'succeeded' && paymentType) {
      // @ts-ignore
      openSnackbar(successMessages[paymentType]);
      startCelebration();
    }
  };

  useEffect(() => {
    if (
      sessionStorage.getItem('Firebase-Authorization') &&
      window.location.pathname === '/incremental'
    ) {
      const params = new URLSearchParams(window.location.search);
      securedFetchRequest(`${API_URL}users/getGoogleAuthToken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: params.get('code'),
        }),
      })
        .then(() => window.close())
        .catch((err) => {
          process.env.NODE_ENV === 'development' && console.error(err);
          Sentry.withScope(function (scope) {
            scope.setTag('section', 'MainRouter.tsx useEffect()');
            Sentry.captureException(err);
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname, user]);

  const renderHomePage = (screenProps: $TSFixMe) => {
    if ((window as $TSFixMe).OVERRIDE_USER_ID) {
      return (
        <LazyMainContainer>
          <LazyProWebsite {...screenProps} />;
        </LazyMainContainer>
      );
    } else {
      if (!user) {
        return <LazyMarketingPage container />;
      } else if (user.userType === 'REQUESTER') {
        return (
          <LazyMainContainer>
            <LazyHomeownerDashboard />
          </LazyMainContainer>
        );
      } else if (user.userType === 'BIDDER') {
        return (
          <LazyMainContainer>
            <LazyContractorDashboard />
          </LazyMainContainer>
        );
      }
    }
  };

  const closeWrongAccountForQuote = () => {
    localStorage.removeItem('bidmiiProUid');
    setWrongAccountForQuoteOpen(false);
  };

  const switchAccountForQuote = () => {
    // Keep the stored quote path across the sign-out storage wipe so the redirect
    // still fires once they sign back in with the right account.
    const migrateTarget = localStorage.getItem('bidmiiProUid');
    signOut(firebaseAuth)
      .then(() => {
        clearStorageOnSignOut();
        if (migrateTarget) {
          localStorage.setItem('bidmiiProUid', migrateTarget);
        }
        setUser(null as any);
        setSavedProjectsFilter(false);
        setWrongAccountForQuoteOpen(false);
        history.push('/login');
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);
        Sentry.withScope(function (scope) {
          scope.setTag('section', 'MainRouter.tsx switchAccountForQuote()');
          Sentry.captureException(err);
        });
      });
  };

  return (
    <>
      <CssBaseline />
      <StandardDialog
        open={wrongAccountForQuoteOpen}
        onClose={closeWrongAccountForQuote}
        secondaryAction={
          <SecondaryButton onClick={closeWrongAccountForQuote}>
            NOT NOW
          </SecondaryButton>
        }
        primaryAction={
          <PrimaryButton onClick={switchAccountForQuote}>
            SWITCH ACCOUNT
          </PrimaryButton>
        }>
        <div>
          <DialogTitle>This quote was sent to you as a customer</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              A contractor sent you a quote for work at your home, but the
              account you are signed in with is not a homeowner account, so the
              quote cannot be shown here. Switch to the homeowner account that
              uses the email address the quote was sent to, or create one with
              that email, and the quote will be waiting for you.
            </Typography>
          </DialogContent>
        </div>
      </StandardDialog>
      <Switch>
        {/* @ts-ignore */}
        <Route path="/new/user" component={LazyNewUserRouter} />
        {/* Unsecured route for review workflow with code parameter */}
        <Route path="/review/:rfpId" component={ReviewWorkflow} />
        {/* @ts-ignore */}
        <ProtectedRoute exact path="/review" component={<LazyReviewWizard />} />
        {/* This was meant to act as a bandaid solution for an error in share urls, remove ASAP pending review */}
        <Route
          path="/share"
          render={() => {
            // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
            const shareUrl = `${SERVER_URL.substring(
              0,
              // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
              SERVER_URL.length - 1,
            )}${window.location.pathname}${window.location.search}`;
            window.location.href = shareUrl;
            return null;
          }}
        />
        {/* Public Bidmii Pro landing page. Only visitors ProtectedRoute
            would have bounced to /login see it; anyone with an auth signal
            (logged in, mid sign-in, or a native webview's `f` token) keeps
            today's behavior: the subscription checkout. */}
        <Route
          exact
          path="/bidmii-pro"
          render={() =>
            hasActiveAuthSignal() ? (
              <LazySubscriptionsCheckout />
            ) : (
              <LazyMainContainer>
                <LazyBidmiiProLandingPage />
              </LazyMainContainer>
            )
          }
        />
        {/* Logged-out visitors following a /plans link get the landing
            page's pricing section instead of a login wall. */}
        <Route
          exact
          path="/bidmii-pro/plans"
          render={() =>
            hasActiveAuthSignal() ? (
              <LazySubscriptionsCheckout />
            ) : (
              <Redirect
                to={{
                  pathname: '/bidmii-pro',
                  hash: '#plans',
                  search: window.location.search,
                }}
              />
            )
          }
        />
        {/* Checkout and every other /bidmii-pro subpath stay login-only. */}
        {/* @ts-ignore */}
        <ProtectedRoute
          path="/bidmii-pro"
          component={LazySubscriptionsCheckout}
        />
        <Route exact path="/login" component={LazyLogin} />
        <Route exact path="/register" render={() => <LazyLogin register />} />
        {/* Used in mobile app WebViews */}
        <Route exact path="/support" component={LazySupport} />
        <Route
          exact
          path="/payments-account"
          component={StripeAccountSettings}
        />
        <Route exact path="/locations/cities" component={LazyLocationsList} />
        <Route
          exact
          path="/locations/cities/:province/:city"
          component={LazyCategoriesList}
        />
        <Route path="/m" render={() => <LazyMarketingPage container />} />
        <Route path="/forms" component={LazyFormsRouter} />
        <Route
          path="/service-orders/:uid/checkout"
          component={ServiceOrderCheckout}
        />
        <Route exact path="/" render={renderHomePage} />
        <Route path="/" component={LazyMain} />
      </Switch>
      {window.outerWidth > 600 && !(window as $TSFixMe).OVERRIDE_USER_ID && (
        <LazySupportPopup />
      )}
      <BidmiiSnackbar />
      <UploadFailureSnackbarListener />
      <BidmiiCustomLoader />
      {celebrationAnimation}
    </>
  );
};

export default MainRouter;
